import { useState, useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { AuthContext } from '~/context/auth';
import toast from 'react-hot-toast';

import { createAccount } from '~/services/account';

import {
    TextInput,
    SelectInput,
    PhoneInput,
} from '~/shared/components/form/Inputs';
import { languageOptions } from '../../shared/config';
import { CheckBox } from '~/shared/components/form/CheckBox';
import { ProgressBar } from './ProgressBar';
import {
    Container,
    Button,
    Card,
    CardBody,
    Col,
    Row,
    Form,
    FormGroup,
    Input,
    Label,
} from 'reactstrap';
import { fetchCountries } from '../../services/hhl';
import { fetchReferralOptions } from '../../services/options';

import { SelectOption } from '~/types/globals';
import { Loading } from '../../shared/components/Loading';

export const AccountSetup = ({}: any) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [outsideTheUS, setOutsideTheUS] = useState<boolean>(false);
    const [languageOption, setLanguageOption] = useState<string>();
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
    const [countries, setCountries] = useState<SelectOption[]>([]);
    const [referralOptions, setReferralOptions] = useState<SelectOption[]>([]);
    const [referralOption, setReferralOption] = useState<string>('');
    const [{ user }, userDispatch] = useContext(AuthContext);
    const {
        register,
        handleSubmit,
        watch,
        control,
        reset,
        setValue,
        getValues,
        formState: { errors, isSubmitting, isValid },
    } = useForm({
        defaultValues: {
            first_name: '',
            last_name: '',
            phone: '',
            email: '',
            zip_code: '',
            preferred_language: '',
            referral: '',
            country: '',
        },
        mode: 'onBlur',
    });

    // Mapping of frontend labels to database codes/IDs
    const referralMapping = [
        { label: 'Medical professional referral', codes: ['financial', 'social_worker', 'case_manager', 'tx_coord'] },
        { label: 'Friend or family member', codes: ['word_of_mouth'] },
        { label: 'Social Media', codes: ['social_media'] },
        { label: 'Search', codes: ['web_search'] },
        { label: 'Other', codes: ['other_prof'] },
    ];

    useEffect(() => {
        // Clear any token that may have persisted from a previous login
        localStorage.removeItem('token');
        userDispatch({
            type: 'set_user',
            user: null,
        });
        
        const loadData = async () => {
            try {
                // Fetch countries
                const countriesRes = await fetchCountries();
                const countryOpts = countriesRes.map((opt: any) => {
                    return {
                        label: opt.name,
                        value: opt.id,
                    };
                });
                setCountries(countryOpts);

                // Fetch and filter referral options
                const referralRes = await fetchReferralOptions();
                const filteredReferrals: SelectOption[] = [];
                
                // For each mapping, find the first matching database entry
                referralMapping.forEach(mapping => {
                    for (const code of mapping.codes) {
                        const dbOption = referralRes.find((opt: any) => opt.code === code);
                        if (dbOption) {
                            filteredReferrals.push({
                                label: mapping.label,
                                value: dbOption.id,
                            });
                            break; // Use first match for each category
                        }
                    }
                });
                
                setReferralOptions(filteredReferrals);
            } catch (error) {
                console.error('Error loading options:', error);
                // Fallback to basic options if API fails
                setReferralOptions([
                    { label: 'Social Media', value: '4' }, // Known ID for social_media
                    { label: 'Search', value: '5' }, // Known ID for web_search
                    { label: 'Friend or family member', value: '9' }, // Known ID for word_of_mouth
                    { label: 'Other', value: '3' }, // Known ID for other_prof
                ]);
            } finally {
                setLoading(false);
            }
        };
        
        loadData();
    }, []);

    useEffect(() => {
        if (languageOption && languageOption !== 'other') {
            setValue('preferred_language', languageOption);
        }
    }, [languageOption]);

    useEffect(() => {
        if (referralOption) {
            setValue('referral', referralOption);
        }
    }, [referralOption]);

    const onSubmit = async (data: any) => {
        // Create the account
        try {
            const resp = await createAccount(data);
            if (resp.detail) {
                toast.error(resp.detail);
            } else {
                setShowConfirmation(true);
            }
        } catch (e) {
            console.log('e: ', e);
            toast.error('There was a problem creating your account');
        }
    };
    console.log('zip required', !outsideTheUS);
    console.log('isvalid', isValid);

    const CreateAccountForm = () => {
        return (
            <div>
                <h3 className='mb-5 text-center'>
                    Before we get started, let's set up your Help Hope Live user
                    profile
                </h3>
                <p className='text-center'>
                    This will allow you to pick up where you left off if you
                    cannot finish the application right now.
                </p>

                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Card
                        className='box-shadow my-5'
                        style={{
                            maxWidth: '400px',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                        }}
                    >
                        <CardBody>
                            <TextInput
                                name='first_name'
                                label='First Name'
                                control={control}
                                required={true}
                                ariaRequired={true}
                                maxLength={50}
                            />
                            <TextInput
                                name='last_name'
                                label='Last Name'
                                control={control}
                                required={true}
                                ariaRequired={true}
                                maxLength={50}
                            />
                            <TextInput
                                name='email'
                                label='Email'
                                type='email'
                                control={control}
                                required={true}
                                ariaRequired={true}
                                helperText={`You will use this email to log into your profile and receive communications from Help Hope Live.`}
                                maxLength={100}
                            />
                            <PhoneInput
                                name='phone'
                                label='Phone Number'
                                control={control}
                                required={true}
                                ariaRequired={true}
                            />

                            {!outsideTheUS ? (
                                <TextInput
                                    name='zip_code'
                                    label='Zip Code'
                                    type='zip'
                                    control={control}
                                    required={!outsideTheUS}
                                    ariaRequired={!outsideTheUS}
                                    helperText={`Our nonprofit supports fundraising campaigns in the U.S. and
                    Puerto Rico.`}
                                    maxLength={10}
                                    // disabled={outsideTheUS}
                                />
                            ) : (
                                <div />
                            )}

                            <FormGroup check className='mb-3 ps-0'>
                                <CheckBox
                                    name='us_address'
                                    label='I am from outside the US'
                                    value={outsideTheUS}
                                    onChange={() =>
                                        setOutsideTheUS(!outsideTheUS)
                                    }
                                />
                            </FormGroup>
                            {outsideTheUS && (
                                <SelectInput
                                    control={control}
                                    required={outsideTheUS}
                                    name='country'
                                    label='Country'
                                    defaultLabel={null}
                                    options={countries}
                                />
                            )}
                            <FormGroup tag='fieldset' className='mb-3'>
                                <Label>Preferred Language</Label>
                                <FormGroup check>
                                    {languageOptions.map((l, i) => {
                                        return (
                                            <div key={i}>
                                                <Label check>
                                                    <Input
                                                        type='radio'
                                                        value={l.value}
                                                        onChange={() =>
                                                            setLanguageOption(
                                                                l.value
                                                            )
                                                        }
                                                        name='preferred_language'
                                                        checked={
                                                            languageOption ===
                                                            l.value
                                                        }
                                                    />
                                                    {l.label}
                                                </Label>
                                            </div>
                                        );
                                    })}
                                </FormGroup>

                                {languageOption === 'other' && (
                                    <Input
                                        name='preferred_language'
                                        required={true}
                                        aria-required={true}
                                        onChange={(e) =>
                                            setValue(
                                                'preferred_language',
                                                e.target.value
                                            )
                                        }
                                    />
                                )}
                            </FormGroup>

                            <FormGroup tag='fieldset' className='mb-3'>
                                <Label>Lastly, how did you hear about Help Hope Live? *</Label>
                                <FormGroup check>
                                    {referralOptions.map((option, index) => {
                                        return (
                                            <div key={index}>
                                                <Label check>
                                                    <Input
                                                        type='radio'
                                                        value={option.value}
                                                        onChange={() =>
                                                            setReferralOption(option.value)
                                                        }
                                                        name='referral'
                                                        checked={
                                                            referralOption === option.value
                                                        }
                                                        required={true}
                                                    />
                                                    {option.label}
                                                </Label>
                                            </div>
                                        );
                                    })}
                                </FormGroup>
                            </FormGroup>
                        </CardBody>
                    </Card>

                    <div className='text-center'>
                        <Button
                            type='submit'
                            color='primary'
                            disabled={isSubmitting || !isValid || !referralOption}
                        >
                            {isSubmitting ? `Saving` : `Next`}
                        </Button>
                    </div>
                </Form>
            </div>
        );
    };

    const Confirmation = () => {
        const noEmailUrl =
            import.meta.env.NO_EMAIL_URL ||
            'https://dev-helphopelive.pantheonsite.io/didnt-get-an-email/';
        return (
            <div className='text-center'>
                <h3 className='mb-5'>
                    Thank you! Check your email to finish setting up your
                    profile.
                </h3>
                <p>
                    We've emailed you a link to create a password to complete
                    your profile.
                </p>
                <div className='d-grid d-md-block gap-2'>
                    <Button
                        color='link'
                        onClick={() => window.open(noEmailUrl, '_blank')}
                    >
                        I didn't get an email
                    </Button>
                </div>
            </div>
        );
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <Container>
            <ProgressBar selectedStep='create-account' />

            <Row className='justify-content-center py-5'>
                <Col md='6'>
                    {!showConfirmation ? (
                        <CreateAccountForm />
                    ) : (
                        <Confirmation />
                    )}
                </Col>
            </Row>
        </Container>
    );
};
